import { Button, Tooltip, useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Reporting } from '@finley-technologies/finley-types';

import {
  FundingRequestContextValue,
  useFundingRequestTemplateContextV1,
} from '../../hooks/useFundingRequestTemplateContextV1';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType,
  useProduceReportingGenerateMessageMutation,
} from 'packages/reporting/requests/reporting.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type GeneratePreviewFundingNoticeWorkleaseParams = {
  setIsLoading: (isLoading: boolean) => void;
  fundingRequestContext: FundingRequestContextValue;
  reportName: string;
  userOrganizationId: string;
  capitalProviderOrganizationId: string;
};

export const generatePreviewFundingNoticeWorklease = ({
  fundingRequestContext,
  reportName,
  userOrganizationId,
  capitalProviderOrganizationId,
}: GeneratePreviewFundingNoticeWorkleaseParams): Reporting.FundingNoticeReportingGenerateQaWorklease => {
  const {
    creditFacility: { id: creditFacilityId },
    context: {
      creditAgreementId,
      mode,
      data: {
        expectedSubmission: { draw },
        resolvedLoan,
      },
    },
  } = fundingRequestContext;

  const worklease: Reporting.FundingNoticeReportingGenerateQaWorklease = {
    initiatingOrganizationId: userOrganizationId,
    creditFacilityId,
    creditAgreementId,
    reportName,
    reportingDate: commonDate.currentDateStr(),
    mode: 'QA',
    reportTemplateFileType: 'docx',
    reportConfigType: 'fundingNotice',
    config: {
      fundingRequestMode: mode,
      drawSubmission: draw,
      capitalProviderOrganizationId,
      creditFacilityId,
      existingLoanId: resolvedLoan?.id ?? undefined,
    },
  };

  return worklease;
};

type DownloadFundingNoticeButtonBodyParams = {
  reportName: string;
  capitalProviderOrganizationId: string;
};
export const DownloadFundingNoticeButtonBody = ({
  reportName,
  capitalProviderOrganizationId,
}: DownloadFundingNoticeButtonBodyParams) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const fundingRequestContext = useFundingRequestTemplateContextV1();

  const [
    currentPollingWorkleaseRecordId,
    setCurrentPollingWorkleaseRecordId,
  ] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'no-cache',
  });

  const [
    getReportS3DownloadUrlByWorklease,
    { data: reportS3DownloadUrlByWorkleaseData },
  ] = useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType({
    pollInterval: currentPollingWorkleaseRecordId ? 1000 : 0, // poll every second if 'currentPollingReport' exists
    fetchPolicy: 'no-cache',
  });

  const generatePreviewFundingNotice = useCallback(async () => {
    setIsLoading(true);

    const worklease = await generatePreviewFundingNoticeWorklease({
      reportName,
      userOrganizationId: user.organizationId,
      setIsLoading,
      fundingRequestContext,
      capitalProviderOrganizationId,
    });

    const { data } = await produceGenerateMessage({
      variables: {
        worklease,
        parameters: null,
      },
    });
    finleyAssert(
      data?.result,
      'Expecting a result from "produceGenerateMessage".',
    );

    setCurrentPollingWorkleaseRecordId(data.result.recordId);

    getReportS3DownloadUrlByWorklease({
      variables: {
        reportConfigType: 'fundingNotice',
        reportingWorkleaseRecordId: data.result.recordId,
      },
    });
  }, [
    produceGenerateMessage,
    user.organizationId,
    reportName,
    getReportS3DownloadUrlByWorklease,
    capitalProviderOrganizationId,
    fundingRequestContext,
  ]);

  useEffect(() => {
    const handleWorkleaseStatus = async () => {
      // if worklease is initialized state, return and continue polling
      if (
        !reportS3DownloadUrlByWorkleaseData?.getReportS3DownloadUrlByReportingWorkleaseRecordId ||
        reportS3DownloadUrlByWorkleaseData
          .getReportS3DownloadUrlByReportingWorkleaseRecordId
          .workleaseStatus === 'initialized'
      ) {
        return;
      }

      const {
        getReportS3DownloadUrlByReportingWorkleaseRecordId: {
          workleaseStatus,
          s3DownloadUrl,
          error,
        },
      } = reportS3DownloadUrlByWorkleaseData;

      // if worklease is in error state, toast error and stop polling
      if (workleaseStatus === 'error' || !s3DownloadUrl) {
        const errorToastDescription =
          commonUtils.EnvUtils.isCustomerFacing() || !error ? undefined : error;

        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [report:${reportName}]`,
          description: errorToastDescription,
        });

        setIsLoading(false);
        setCurrentPollingWorkleaseRecordId(undefined);
        return;
      }

      // if worklease is in success state, download file from S3 url and stop polling
      if (workleaseStatus === 'success') {
        setCurrentPollingWorkleaseRecordId(undefined);

        try {
          const response = await axios({
            url: s3DownloadUrl,
            method: 'GET',
            responseType: 'blob',
          });

          const fileBlob = new Blob([response.data], {
            type: response.data.type,
          });
          const fileName = `${reportName}-preview-${commonDate.currentDateStr()}`;
          FileSaver.saveAs(fileBlob, fileName);
        } catch (err) {
          errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
            ? 'Failed to retrieve report preview. Please contact Finley for support.'
            : JSON.stringify(err);

          toast(errorToastConfig);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (currentPollingWorkleaseRecordId) {
      // if there is a worklease we are currently polling for,
      // handle the worklease results
      handleWorkleaseStatus();
    }
  }, [
    reportName,
    reportS3DownloadUrlByWorkleaseData,
    toast,
    currentPollingWorkleaseRecordId,
  ]);

  return (
    <Tooltip label="Generate and download notice preview">
      <Button
        variant="v2-ghost"
        onClick={async () => {
          await generatePreviewFundingNotice();
        }}
        isLoading={isLoading}
      >
        <Icon icon="arrow-down-to-bracket" />
      </Button>
    </Tooltip>
  );
};

type DownloadFundingNoticeButtonParams = {
  reportName: string;
  capitalProviderOrganizationId: string;
};
export const DownloadPreviewFundingNoticeButton = ({
  reportName,
  capitalProviderOrganizationId,
}: DownloadFundingNoticeButtonParams) => {
  return (
    <>
      {reportName ? (
        <DownloadFundingNoticeButtonBody
          reportName={reportName}
          capitalProviderOrganizationId={capitalProviderOrganizationId}
        />
      ) : (
        <Tooltip label="Funding notice is not configured">
          <Button isDisabled variant="v2-ghost">
            <Icon icon="arrow-down-to-bracket" />
          </Button>
        </Tooltip>
      )}
    </>
  );
};
